import { useEffect, useState } from "react"
import { useField, useFormikContext } from "formik"
import { makeStyles } from "tss-react/mui"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { FormHelperText, IconButton, Stack, Typography } from "@mui/material"
import { faRepeat, faPen, faTrash } from "@fortawesome/pro-solid-svg-icons"

import { LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"
import { useAppLocalization } from "../../../../../../modules/localization/components/AppLocalizationProvider"
import { useDateTime } from "../../../../../../modules/localization/useDateTime"
import { StandardButton } from "../../../../buttons/StandardButton"
import { ConfirmModal } from "../../../../dialogs/ConfirmModal"

import { useCreateOrderFormConfig } from "../../../mainForm/CreateOrderFormContext"
import { DeliveryTimesField, DynamicConfigKey, MeetingServiceTabValues } from "../../form/formTypes"
import { RepeatOrderDialog, RepeatOrderFormValues } from "../meetingDetails/RepeatOrderDialog"
import { getFormikFieldError } from "../../form/helpers"
import { formatRecurrence, getRecurrenceErrorId } from "./helpers"

const useStyles = makeStyles()((theme) => ({
    icon: {
        color: theme.palette.tailwind.slate[400],
    },
    editButton: {
        color: theme.palette.tailwind.blue[500],
    },
    iconRepeat: {
        marginTop: ".25rem",
    },
    button: {
        paddingLeft: ".25rem",
    },
}))

type RecurrenceProps = Readonly<{
    title?: string
    description: string
    label?: string
    defaultButtonClass?: string
}>

export function Recurrence({ title = "", label, description, defaultButtonClass }: RecurrenceProps) {
    const { classes, cx } = useStyles()
    const { l10n } = useAppLocalization()
    const dateTimeFactory = useDateTime()
    const { flowConfig, orderRecurrencePattern } = useCreateOrderFormConfig()

    // The PATTERN locks the rule, not the Outlook link: an Outlook meeting without one is an
    // ordinary single meeting, and Manager recurrence has always been available on those.
    const hasOutlookRecurrence = Boolean(flowConfig?.order?.meetingDetails?.recurrence?.pattern)
    const isRecurrenceManagedInOutlook = hasOutlookRecurrence || Boolean(orderRecurrencePattern)

    const [{ value: deliveries }] = useField<DeliveryTimesField | undefined>(DynamicConfigKey.MULTIPLE_DELIVERIES)
    const [{ value: recurrenceValue }, meta, recurrenceHelpers] = useField<Partial<RepeatOrderFormValues> | undefined>({
        name: DynamicConfigKey.RECURRENCY,
        validate: (value: Partial<RepeatOrderFormValues> | null | undefined) => {
            const errorId = !isRecurrenceManagedInOutlook && value ? getRecurrenceErrorId(value) : null
            return errorId ? l10n.getString(errorId) : undefined
        },
    })
    const { submitCount } = useFormikContext<MeetingServiceTabValues>()

    const [repeatOrderDialog, setRepeatOrderDialog] = useState<boolean>(false)
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState<boolean>(false)

    const deliveryStartDate = deliveries?.deliveryDate
        ? dateTimeFactory.fromISO(deliveries.deliveryDate).startOf("day")
        : null

    // the start date follows the delivery date
    useEffect(() => {
        if (isRecurrenceManagedInOutlook || !recurrenceValue?.startDate || !deliveryStartDate) return
        if (recurrenceValue.startDate.hasSame(deliveryStartDate, "day")) return

        recurrenceHelpers.setTouched(true, false)
        recurrenceHelpers.setValue({ ...recurrenceValue, startDate: deliveryStartDate })
    }, [deliveries?.deliveryDate, recurrenceValue?.startDate, isRecurrenceManagedInOutlook])

    const handleOpenDialog = () => {
        if (isRecurrenceManagedInOutlook) return
        setRepeatOrderDialog(true)
    }

    const handleSubmitRecurrence = (values: Partial<RepeatOrderFormValues>) => {
        setRepeatOrderDialog(false)
        recurrenceHelpers.setValue(values)
    }

    const handleDelete = () => {
        recurrenceHelpers.setValue(undefined)
    }

    const { helperText } = getFormikFieldError(meta, submitCount)

    return (
        <>
            <Stack gap="0.25rem">
                <Stack gap="1rem" width="100%">
                    <Stack flexDirection="row" gap="1rem" alignItems="center" justifyContent="space-between">
                        {title && <Typography variant="subtitle1">{title}</Typography>}
                        {!isRecurrenceManagedInOutlook && !recurrenceValue && (
                            <StandardButton
                                type="button"
                                color="blue"
                                variant="text"
                                size="small"
                                fullWidth={false}
                                onClick={handleOpenDialog}
                                startIcon={<FontAwesomeIcon icon={faRepeat} />}
                                updateDefaultClasses={cx(defaultButtonClass, classes.button)}
                            >
                                {label || description}
                            </StandardButton>
                        )}
                    </Stack>
                    {/* Also for a pattern from the loaded order — the edit button is gone either way. */}
                    {isRecurrenceManagedInOutlook && (
                        <Stack flexDirection="row" gap=".5rem">
                            <FontAwesomeIcon icon={faRepeat} className={cx(classes.icon, classes.iconRepeat)} />
                            <LocalizedStrict id="create-order-recurrence-outlook-managed">
                                <Typography variant="body1">This meeting repeats (managed in Outlook)</Typography>
                            </LocalizedStrict>
                        </Stack>
                    )}
                    {!isRecurrenceManagedInOutlook && recurrenceValue && (
                        <Stack gap=".5rem">
                            <Stack flexDirection="row" gap=".5rem">
                                <FontAwesomeIcon icon={faRepeat} className={cx(classes.icon, classes.iconRepeat)} />
                                <Typography variant="body1">
                                    {formatRecurrence({ values: recurrenceValue, l10n })}
                                </Typography>
                            </Stack>
                            <Stack flexDirection="row" gap=".75rem" alignItems="center">
                                <IconButton className={classes.editButton} size="small" onClick={handleOpenDialog}>
                                    <FontAwesomeIcon icon={faPen} />
                                </IconButton>
                                <IconButton
                                    className={classes.icon}
                                    size="small"
                                    onClick={() => setIsDeleteConfirmOpen(true)}
                                >
                                    <FontAwesomeIcon icon={faTrash} />
                                </IconButton>
                            </Stack>
                        </Stack>
                    )}
                </Stack>
                {!isRecurrenceManagedInOutlook && helperText && <FormHelperText error>{helperText}</FormHelperText>}
            </Stack>

            {!isRecurrenceManagedInOutlook && repeatOrderDialog && (
                <RepeatOrderDialog
                    repeatOrderDialog={repeatOrderDialog}
                    onClose={() => {
                        setRepeatOrderDialog(false)
                    }}
                    initialValues={recurrenceValue}
                    handleSubmitRecurrence={handleSubmitRecurrence}
                    startDate={deliveryStartDate}
                />
            )}

            {isDeleteConfirmOpen && (
                <LocalizedStrict
                    id="create-order-drawer-repeat-order-delete-confirm-dialog"
                    attrs={{ title: true, buttonLabelSubmit: true }}
                >
                    <ConfirmModal
                        open={isDeleteConfirmOpen}
                        onClose={() => setIsDeleteConfirmOpen(false)}
                        handleClick={handleDelete}
                        title="Delete repeat"
                        message={
                            <LocalizedStrict id="create-order-drawer-repeat-order-delete-confirm-dialog-message">
                                <Typography variant="body1">
                                    The repeat and any deselected days will be removed. The order itself is kept.
                                </Typography>
                            </LocalizedStrict>
                        }
                        buttonLabelSubmit="Delete"
                    />
                </LocalizedStrict>
            )}
        </>
    )
}
